import { ChevronDown } from 'lucide-react'
import { type ReactNode, useEffect, useId, useState } from 'react'
import { useI18n } from '../../i18n'
import { cn } from '../ui/utils'

export interface CollapsibleSectionProps {
  title: string
  icon?: ReactNode
  children: ReactNode
  defaultExpanded?: boolean
  collapseSignal?: number
  headerActions?: ReactNode
}

export function CollapsibleSection({
  title,
  icon,
  children,
  defaultExpanded = true,
  collapseSignal,
  headerActions,
}: CollapsibleSectionProps) {
  const { t } = useI18n()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const contentId = useId()

  useEffect(() => {
    if (collapseSignal === undefined) return
    setIsExpanded(false)
  }, [collapseSignal])

  return (
    <div className={cn('cv-editor-section', isExpanded && 'cv-editor-section-expanded')}>
      <div className="cv-editor-section-header">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="cv-editor-section-toggle"
          aria-expanded={isExpanded}
          aria-controls={contentId}
          aria-label={t(isExpanded ? 'actions.collapse' : 'actions.expand', { item: title })}
        >
          <ChevronDown
            className={cn('cv-editor-section-chevron size-4', isExpanded && 'rotate-180')}
          />
          {icon && <span className="cv-editor-section-icon">{icon}</span>}
          <span className="cv-editor-section-title">{title}</span>
        </button>

        {headerActions && (
          <div
            className="cv-editor-section-actions"
            aria-label={`${title} ${t('builder.controls')}`}
          >
            {headerActions}
          </div>
        )}
      </div>

      <div
        id={contentId}
        hidden={!isExpanded}
        className={cn('cv-editor-section-content', isExpanded && 'cv-editor-section-content-open')}
      >
        <div className="cv-editor-section-content-inner">
          <div className="cv-editor-section-body">{children}</div>
        </div>
      </div>
    </div>
  )
}
